/**
 * What a refused claim tells the player, and what the refusal implies about the
 * rebate figures already on screen.
 *
 * Out of the mutation handler for the same reason `card-copy` is out of the
 * card: what somebody is told when their money does not arrive is a rule, and a
 * rule wants a test. Inside the handler it is a React callback around `toast`,
 * which only a render can reach.
 */
import { ApiError, mapApiError, type Translator } from '@/lib/api/error.js';

export interface ClaimFailureNotice {
  /** `info` for a refusal that is a state the card itself talks about. */
  tone: 'info' | 'error';
  message: string;
}

/**
 * Refusals whose wording lives in the promo namespace rather than the error
 * catalog, because each one names a state the promo screen already has copy
 * for. Everything else resolves through the catalog, so a code added on the
 * server needs a translation here and no code change.
 */
const PROMO_NAMESPACE_MESSAGES: Record<string, string> = {
  'PROMO-030': 'toast.alreadyClaimed',
  'PROMO-020': 'toast.capReached',
  'PROMO-031': 'toast.depositRequired',
};

/** `t` must be bound to the `promo` namespace — `mapApiError` qualifies its own. */
export function claimFailureNotice(err: unknown, t: Translator): ClaimFailureNotice {
  if (err instanceof ApiError && err.code) {
    // Own entries only: a plain index read answers `constructor` and `toString`
    // with what Object.prototype carries, and the toast then renders the source
    // of a native function at the player.
    if (Object.hasOwn(PROMO_NAMESPACE_MESSAGES, err.code)) {
      return { tone: 'info', message: t(PROMO_NAMESPACE_MESSAGES[err.code] as string) };
    }
  }
  return { tone: 'error', message: mapApiError(err, t) };
}

/**
 * True when the refusal proves every rebate preview on screen is out of date.
 *
 * PROMO-077 refuses a rebate because the sibling rebate promotion already took
 * this period. The card that was clicked cannot have known — it offered the
 * claim from a preview taken before the other one landed — so the refusal is
 * itself the news that both previews are stale.
 */
export function refusalOutdatesRebatePreview(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'PROMO-077';
}
